import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});
  const [cooldowns, setCooldowns] = useState({});

  // 🔥 API URL 설정 - 실제 MQTT 전송을 위해 배포 서버 사용
  const getApiUrl = () => {
    // 🔥 항상 배포 서버를 사용하여 실제 MQTT 전송
    return 'https://missing-person.app.noaplc.kr:8080';

    // 주석: 로컬 테스트가 필요한 경우 아래 주석 해제
    /*
    const currentHost = window.location.host;
    const protocol = window.location.protocol;
    
    if (currentHost.includes('localhost') || currentHost.includes('127.0.0.1')) {
      return 'http://localhost:3001';
    } else {
      return `${protocol}//${currentHost.replace(':3000', ':8080')}`;
    }
    */
  };

  const setButtonLoading = (buttonId, isLoading) => {
    setLoading(prev => ({ ...prev, [buttonId]: isLoading }));
  };

  const setResult = (buttonId, result) => {
    setResults(prev => ({ ...prev, [buttonId]: result }));
  };

  const startCooldown = (buttonId) => {
    setCooldowns(prev => ({ ...prev, [buttonId]: 30 }));

    const timer = setInterval(() => {
      setCooldowns(prev => {
        const newTime = prev[buttonId] - 1;
        if (newTime <= 0) {
          clearInterval(timer);
          return { ...prev, [buttonId]: 0 };
        }
        return { ...prev, [buttonId]: newTime };
      });
    }, 1000);
  };

  // 🔥 일반 API 호출 (토큰 필요)
  const handleApiCall = async (buttonId, endpoint, data, successMessage) => {
    setButtonLoading(buttonId, true);
    setResult(buttonId, null);

    try {
      const apiBaseUrl = getApiUrl();
      const fullUrl = `${apiBaseUrl}/api/test/${endpoint}`;

      console.log('🔍 API 호출:', {
        buttonId,
        endpoint,
        fullUrl,
        data,
        hasToken: !!localStorage.getItem('token')
      });

      const response = await axios.post(fullUrl, data, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        timeout: 30000
      });

      console.log('✅ API 응답:', response.data);

      setResult(buttonId, {
        success: true,
        message: `✅ ${successMessage} → MQTT 토픽 전송 완료!`,
        data: response.data,
        timestamp: new Date().toLocaleTimeString()
      });

      startCooldown(buttonId);

    } catch (error) {
      console.error('❌ API 호출 에러:', error);

      const errorMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        '요청 처리 중 오류가 발생했습니다';

      setResult(buttonId, {
        success: false,
        message: `❌ ${errorMessage}`,
        error: error.response?.data,
        timestamp: new Date().toLocaleTimeString(),
        debugInfo: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url
        }
      });

      startCooldown(buttonId);

    } finally {
      setButtonLoading(buttonId, false);
    }
  };

  // 🔥 실종자 신고 테스트 (토큰 불필요)
  const handleMissingPersonTest = async () => {
    const buttonId = 'missing-person';
    setButtonLoading(buttonId, true);
    setResult(buttonId, null);

    try {
      const apiBaseUrl = getApiUrl();
      const fullUrl = `${apiBaseUrl}/api/test/send-realistic-missing-person`;

      const data = {
        name: "차정원",
        age: 31,
        gender: "F",
        region: "울산광역시 울주군 신전2길",
        includePhoto: true
      };

      console.log('🔍 실종자 API 호출:', {
        fullUrl,
        data,
        noToken: true
      });

      const response = await axios.post(fullUrl, data, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
          // Authorization 헤더 제거 (토큰 불필요)
        },
        timeout: 30000
      });

      console.log('✅ 실종자 API 응답:', response.data);

      setResult(buttonId, {
        success: true,
        message: `✅ 실종자 신고가 성공적으로 전송되었습니다 → MQTT 토픽 전송 완료!`,
        data: response.data,
        timestamp: new Date().toLocaleTimeString()
      });

      startCooldown(buttonId);

    } catch (error) {
      console.error('❌ 실종자 API 호출 에러:', error);

      const errorMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        '실종자 신고 전송에 실패했습니다';

      setResult(buttonId, {
        success: false,
        message: `❌ ${errorMessage}`,
        error: error.response?.data,
        timestamp: new Date().toLocaleTimeString(),
        debugInfo: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url
        }
      });

      startCooldown(buttonId);

    } finally {
      setButtonLoading(buttonId, false);
    }
  };

  // 재난문자 테스트 함수들
  const handleTyphoonTest = () => {
    handleApiCall(
      'typhoon',
      'send-realistic-disaster',
      {
        disasterType: "폭염",
        message: "폭염 경보 발효 [울산광역시]"
      },
      '폭염 경보가 성공적으로 전송되었습니다'
    );
  };

  const handleFireTest = () => {
    handleApiCall(
      'fire',
      'send-realistic-disaster',
      {
        disasterType: "화재",
        message: "공장화재 발생 대피하세요 [울산광역시]"
      },
      '화재 경보가 성공적으로 전송되었습니다'
    );
  };

  const handleTrafficTest = () => {
    handleApiCall(
      'traffic',
      'send-realistic-disaster',
      {
        disasterType: "교통사고",
        message: "교통사고로 도로 통제 [울산광역시]"
      },
      '교통사고 알림이 성공적으로 전송되었습니다'
    );
  };

  // 기상특보 테스트 함수들
  const handleHeatWaveAdvisoryTest = () => {
    handleApiCall(
      'heat-advisory',
      'single-warning',
      {
        regionId: "L1082900",
        warningType: "H",
        level: "2",
        action: "issued"
      },
      '폭염주의보가 성공적으로 발령되었습니다'
    );
  };

  const handleHeatWaveWarningTest = () => {
    handleApiCall(
      'heat-warning',
      'single-warning',
      {
        regionId: "L1082900",
        warningType: "H",
        level: "3",
        action: "issued"
      },
      '폭염경보가 성공적으로 발령되었습니다'
    );
  };

  const handleWeatherReleaseTest = () => {
    handleApiCall(
      'weather-release',
      'single-warning',
      {
        regionId: "L1082900",
        warningType: "H",
        level: "2",
        action: "released"
      },
      '기상특보가 성공적으로 해제되었습니다'
    );
  };

  // TestButton 컴포넌트
  const TestButton = ({ id, title, description, onClick, icon }) => {
    const isLoading = loading[id];
    const cooldownTime = cooldowns[id] || 0;
    const isDisabled = isLoading || cooldownTime > 0;

    return (
      <div className="test-card">
        <div className="test-header">
          <span className="test-icon">{icon}</span>
          <h3>{title}</h3>
        </div>
        <p>{description}</p>
        <button
          onClick={onClick}
          disabled={isDisabled}
          className={`test-button ${cooldownTime > 0 ? 'cooldown' : ''}`}
        >
          {isLoading
            ? '전송 중...'
            : cooldownTime > 0
              ? `대기 중... (${cooldownTime}초)`
              : '테스트 실행'
          }
        </button>

        {/* 쿨다운 프로그레스 바 제거됨 - 눈 피로 방지 */}

        {results[id] && (
          <div className={`result ${results[id].success ? 'success' : 'error'}`}>
            <div className="result-header">
              <p>{results[id].message}</p>
              {results[id].timestamp && (
                <small className="timestamp">실행 시간: {results[id].timestamp}</small>
              )}
            </div>
            {results[id].data && (
              <div className="result-details">
                <div className="mqtt-status">
                  📡 MQTT 상태: {results[id].data.mqttStatus || '전송됨'}
                </div>
                {results[id].data.imageStatus && (
                  <div className={`image-status ${results[id].data.imageStatus.includes('포함') ? 'included' : 'excluded'}`}>
                    🖼️ {results[id].data.imageStatus}
                  </div>
                )}
                <details>
                  <summary>📊 전송된 데이터 상세보기</summary>
                  <pre>{JSON.stringify(results[id].data, null, 2)}</pre>
                </details>
              </div>
            )}
            {results[id].debugInfo && (
              <div className="debug-info">
                <details>
                  <summary>🐛 디버그 정보</summary>
                  <pre>{JSON.stringify(results[id].debugInfo, null, 2)}</pre>
                </details>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // 🔥 현재 API URL 표시 컴포넌트
  const ApiStatusIndicator = () => {
    const currentApiUrl = getApiUrl();
    const isLocal = currentApiUrl.includes('localhost');
    const isProduction = currentApiUrl.includes('missing-person.app.noaplc.kr');

    return (
      <div className={`api-status ${isLocal ? 'local' : 'production'}`}>
        <span className="status-indicator">
          {isLocal ? '🟡' : '🟢'}
        </span>
        <span className="status-text">
          API: {currentApiUrl} {isLocal ? '(로컬-시뮬레이션)' : isProduction ? '(배포-실제MQTT)' : '(배포)'}
        </span>
      </div>
    );
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>🧪 실종자 재난 관리 시스템 테스트</h1>
          <div className="header-info">
            <ApiStatusIndicator />
            <div className="user-info">
              <span>환영합니다, {user?.username}님</span>
              <button onClick={logout} className="logout-button">
                로그아웃
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <section className="test-section">
          <h2>👤 실종자 관리 테스트</h2>
          <div className="test-grid">
            <TestButton
              id="missing-person"
              title="실종자 신고 테스트"
              description="차정원(31세, 여성) 실종자 신고를 시스템에 등록하고 MQTT 알림을 발송합니다. (토큰 인증 불필요)"
              onClick={handleMissingPersonTest}
              icon="🚨"
            />
          </div>
        </section>

        <section className="test-section">
          <h2>🌪️ 재난문자 관리 테스트</h2>
          <div className="test-grid">
            <TestButton
              id="typhoon"
              title="폭염 경보"
              description="울산광역시 폭염 경보 발효 알림을 전송합니다."
              onClick={handleTyphoonTest}
              icon="🔥"
            />
            <TestButton
              id="fire"
              title="화재 경보"
              description="울산광역시 공장화재 발생 대피 알림을 전송합니다."
              onClick={handleFireTest}
              icon="🔥"
            />
            <TestButton
              id="traffic"
              title="교통사고 알림"
              description="울산광역시 교통사고로 인한 도로 통제 알림을 전송합니다."
              onClick={handleTrafficTest}
              icon="🚗"
            />
          </div>
        </section>

        <section className="test-section">
          <h2>🌡️ 기상특보 관리 테스트</h2>
          <div className="test-grid">
            <TestButton
              id="heat-advisory"
              title="폭염주의보 발령"
              description="울산광역시 폭염주의보 발령 테스트를 실행합니다."
              onClick={handleHeatWaveAdvisoryTest}
              icon="🟡"
            />
            <TestButton
              id="heat-warning"
              title="폭염경보 발령"
              description="울산광역시 폭염경보 발령 테스트를 실행합니다."
              onClick={handleHeatWaveWarningTest}
              icon="🔴"
            />
            <TestButton
              id="weather-release"
              title="기상특보 해제"
              description="울산광역시 기상특보 해제 테스트를 실행합니다."
              onClick={handleWeatherReleaseTest}
              icon="🟢"
            />
          </div>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;